import React, { useMemo, useState } from "react";
import { Button } from "reactstrap";
import TrafficListDialog from "./TrafficListDialog";
import UrlCopyCell from "./UrlCopyCell";

const RANK = { risk: 0, warning: 1, info: 2 };
const DOT = {
	risk: "monitoring__ai-traffic-signal-dot--risk",
	warning: "monitoring__ai-traffic-signal-dot--warning",
	info: "monitoring__ai-traffic-signal-dot--info",
};

function normalise(trafficSignals, transitions) {
	const out = [];
	for (const s of trafficSignals || []) {
		const items = s.items ?? [];
		out.push({
			key: s.key,
			severity: s.severity || "info",
			title: s.title,
			detail: s.detail,
			items,
			total: s.total ?? items.length,
			metric: "crawls",
		});
	}
	for (const t of transitions || []) {
		out.push({
			key: t.key,
			severity: t.severity,
			title: `${t.count} ${t.label}`,
			detail: t.detail,
			filterNote: t.bot_filtered === false ? t.filter_note : undefined,
			items: t.items ?? [],
			total: t.count,
			metric: t.key === "ranking_not_cited" ? "position" : "none",
		});
	}
	return out.sort(
		(a, b) =>
			(RANK[a.severity] ?? 9) - (RANK[b.severity] ?? 9) || b.total - a.total
	);
}

function SignalRow({ signal }) {
	const [open, setOpen] = useState(false);
	const { items, total } = signal;
	const columns = [
		{
			key: "url",
			label: "Page",
			render: (it) => <UrlCopyCell url={it.url} />,
		},
	];
	if (signal.metric === "crawls") {
		columns.push({
			key: "crawls",
			label: "Crawls",
			align: "right",
			render: (it) => it.crawl_requests ?? "—",
		});
	}
	if (signal.metric === "position") {
		columns.push({
			key: "pos",
			label: "Position",
			align: "right",
			render: (it) => it.position ?? "—",
		});
	}

	return (
		<div className="monitoring__ai-traffic-signal">
			<span
				className={`monitoring__ai-traffic-signal-dot ${
					DOT[signal.severity] || DOT.info
				}`}
			/>
			<div className="monitoring__ai-traffic-signal-body">
				<div className="monitoring__ai-traffic-signal-head">
					<span className="monitoring__ai-traffic-signal-title">{signal.title}</span>
					{items.length > 0 && (
						<Button
							size="sm"
							color="primary"
							className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-signal-view-btn"
							onClick={() => setOpen(true)}
						>
							View {total} URL{total === 1 ? "" : "s"}
						</Button>
					)}
				</div>
				<div className="monitoring__ai-traffic-signal-detail">{signal.detail}</div>
				{signal.filterNote && (
					<div className="monitoring__ai-traffic-signal-filter-note">
						{signal.filterNote}
					</div>
				)}
			</div>
			{open && (
				<TrafficListDialog
					title={signal.title}
					detail={signal.detail}
					rows={items}
					unit="URL"
					unitPlural="URLs"
					pageSize={10}
					rowKey={(it) => it.url}
					searchText={(it) => it.url}
					copyText={(it) => it.url}
					onClose={() => setOpen(false)}
					columns={columns}
				/>
			)}
		</div>
	);
}

export default function SignalsList({ trafficSignals, transitions }) {
	const signals = useMemo(
		() => normalise(trafficSignals, transitions),
		[trafficSignals, transitions]
	);
	if (!signals.length) return null;

	return (
		<div className="monitoring__ai-traffic-kpi-card">
			<h3 className="monitoring__ai-traffic-section-title mb-3">Signals</h3>
			{signals.map((s) => (
				<SignalRow key={s.key} signal={s} />
			))}
		</div>
	);
}
